import type { WebsocketRequest } from '@src/components';

import { wsScanToMap } from '@src/pipes';
import { useEffect, type PropsWithChildren } from 'react';

import { useConstant } from '@src/hooks/useConstant';
import { useObservable } from '@src/hooks/useObservable';
import { useStaticSubscription } from '@src/hooks/useStaticSubscription';
import { BALANCE_TRANSACTION } from '@src/tokens';
import type { CustomerBalanceTransaction } from '@src/types';
import { formattedDateForSubscription } from '@src/utils';
import { map, mergeMap, shareReplay } from 'rxjs';
import { BalanceTransactionsContext } from '../contexts/BalanceTransactionsContext';

type BalanceTransactionsProviderProps = PropsWithChildren<{
  /**
   * If true, the sessionTransactionObservable subscription will be made,
   * observing all balance transactions.
   */
  shouldHaveOpenSubscription: boolean;
}>;

export function BalanceTransactionsProvider({
  children,
  shouldHaveOpenSubscription,
}: BalanceTransactionsProviderProps) {
  const transactionsRequest: WebsocketRequest = useConstant({
    name: BALANCE_TRANSACTION,
    tag: `BalanceTransactionsProviderOnDemand`,
    OnlyMine: true,
    sort_by: '-Timestamp',
  });

  const { data: transactionsObs } = useStaticSubscription<CustomerBalanceTransaction>(transactionsRequest);
  const sessionTransactionsRequest: WebsocketRequest = useConstant({
    name: BALANCE_TRANSACTION,
    tag: `BalanceTransactionsProviderSession`,
    OnlyMine: true,
    sort_by: '-Timestamp',
    StartDate: formattedDateForSubscription(new Date()),
  });
  const { data: sessionTransactionsObsRaw } =
    useStaticSubscription<CustomerBalanceTransaction>(sessionTransactionsRequest);

  // Observable that emits singular transaction updates as they come in.
  // Meaning [transaction,transaction,transaction] becomes [transaction] [transaction] [transaction]
  // This is meant to be used for updates that happen during the user's session.
  const sessionTransactionsObs = useObservable(() => {
    return sessionTransactionsObsRaw.pipe(mergeMap(json => json.data));
  }, [sessionTransactionsObsRaw]);

  const transactionsByIdObs = useObservable(
    () =>
      transactionsObs.pipe(
        wsScanToMap({
          getUniqueKey: transaction => transaction.TransactionID,
          getInsertable: transaction => transaction,
          newMapEachUpdate: false,
        }),
        shareReplay({ bufferSize: 1, refCount: true }) // replay and multicast the output
      ),
    [transactionsObs]
  );

  const transactionsListObs = useObservable(
    () =>
      transactionsByIdObs.pipe(
        // TODO(robertz): not ideal to sort here, but it's the easiest way to get the latest transactions at the top
        map(myMap =>
          Array.from(myMap.values()).toSorted(
            (a, b) => new Date(b.Timestamp!).getTime() - new Date(a.Timestamp!).getTime()
          )
        ),
        shareReplay({ bufferSize: 1, refCount: true }) // replay and multicast the output
      ),
    [transactionsByIdObs]
  );

  useEffect(() => {
    if (!shouldHaveOpenSubscription) {
      return;
    }
    // Have the transactions subscription open.
    // otherwise, the subscription will be opened when the observables are listened/subscribed to in the app.
    const subscription = sessionTransactionsObs.subscribe();
    return () => {
      subscription.unsubscribe();
    };
  }, [sessionTransactionsObs, shouldHaveOpenSubscription]);

  return (
    <BalanceTransactionsContext.Provider
      value={{
        transactionsByIdObs,
        transactionsListObs,
        sessionTransactionsObs,
      }}
    >
      {children}
    </BalanceTransactionsContext.Provider>
  );
}
